import { DocumentTemplateShell } from './TemplateShared';
import {
  TemplateAppendixLayout,
  TemplateSummaryHeader,
} from './TemplateAppendixLayout';
import {
  DASH,
  extractMarkupText,
  formatIsoDate,
  formatPractitionerAddress,
  formatPractitionerNameWithRole,
  formatPractitionerPhone,
  handleTemplateAnchorClick,
} from './templateUtils';
import type { TemplatePatient, TemplatePractitioner } from './templateUtils';

export type MedicationsTemplatePractitioner = TemplatePractitioner;

export interface MedicationsTemplateRow {
  id: string;
  name?: string;
  text?: string;
  statementText?: string;
  status?: string;
  startDatetime?: string;
  endDatetime?: string;
  doseUnit?: string;
  doseValue?: string;
  doseRoute?: string;
  patientInstructions?: string;
  practitioner?: MedicationsTemplatePractitioner;
  dateUploaded?: string;
}

export interface MedicationsTemplateProps {
  medications: MedicationsTemplateRow[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightMedicationId?: string;
  reportTitle?: string;
}

const summaryHeaders = [
  'Medication',
  'Dose',
  'Route',
  'Start',
  'End',
  '',
] as const;

const medicationTableGridClass =
  'grid grid-cols-[minmax(0,2fr)_minmax(0,0.75fr)_minmax(0,0.75fr)_minmax(0,1.1fr)_minmax(0,1.1fr)_minmax(0,0.45fr)]';

const formatDosage = (m: MedicationsTemplateRow) => {
  const parts = [m.doseValue, m.doseUnit].filter(Boolean);
  return parts.length ? parts.join(' ') : DASH;
};

const getMedicationDisplayName = (m: MedicationsTemplateRow, index: number) =>
  m.name?.trim() || m.text?.trim() || `Medication ${index + 1}`;

const getRawMedicationNotes = (
  m: MedicationsTemplateRow,
): string | undefined => {
  if (m.statementText?.trim()) return m.statementText.trim();

  const text = m.text?.trim();
  const name = m.name?.trim();
  if (text && text !== name) return text;

  return undefined;
};

const getMedicationNotes = (m: MedicationsTemplateRow) => {
  const parsed = extractMarkupText(getRawMedicationNotes(m));
  return parsed.trim() || DASH;
};

const getMedicationInstructions = (m: MedicationsTemplateRow) => {
  const parsed = extractMarkupText(m.patientInstructions);
  return parsed.trim() || DASH;
};

const sortMedicationsByRecency = (medications: MedicationsTemplateRow[]) =>
  [...medications].sort((a, b) => {
    const dateA = new Date(a.startDatetime ?? a.endDatetime ?? 0).getTime();
    const dateB = new Date(b.startDatetime ?? b.endDatetime ?? 0).getTime();
    const safeA = Number.isNaN(dateA) ? 0 : dateA;
    const safeB = Number.isNaN(dateB) ? 0 : dateB;
    return safeB - safeA;
  });

const getMedicationTableCellClass = (
  columnIndex: number,
  totalColumns: number,
  variant: 'header' | 'body' = 'body',
) => {
  const borderColor =
    variant === 'header' ? 'border-zinc-300' : 'border-zinc-200';
  const borderClass =
    columnIndex < totalColumns - 1 ? `border-r ${borderColor}` : '';
  const alignClass =
    columnIndex === totalColumns - 1 ? 'text-right' : 'text-left';

  return `px-3 py-2.5 ${borderClass} ${alignClass}`;
};

const SummaryTable = ({
  medications,
  highlightMedicationId,
}: {
  medications: MedicationsTemplateRow[];
  highlightMedicationId?: string;
}) => (
  <div className="overflow-hidden rounded-sm border border-zinc-200">
    <div className={`${medicationTableGridClass} bg-slate-100`}>
      {summaryHeaders.map((header, idx) => (
        <div
          key={`summary-header-${idx}`}
          className={getMedicationTableCellClass(
            idx,
            summaryHeaders.length,
            'header',
          )}
        >
          <p className="text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
            {header}
          </p>
        </div>
      ))}
    </div>
    {medications.map((medication, idx) => {
      const isHighlighted =
        !!highlightMedicationId && medication.id === highlightMedicationId;
      const zebra = idx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
      const rowBg = isHighlighted ? 'bg-amber-100' : zebra;
      const anchorId = `medication-${medication.id}`;
      const pageNumber = idx + 2;
      const cellClass = (columnIndex: number) =>
        getMedicationTableCellClass(columnIndex, summaryHeaders.length);

      return (
        <div
          key={medication.id ?? `m-${idx}`}
          className={`${medicationTableGridClass} border-t border-zinc-200 ${rowBg}`}
        >
          <div className={cellClass(0)}>
            <p className="text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              {getMedicationDisplayName(medication, idx)}
            </p>
          </div>
          <div className={cellClass(1)}>
            <p className="text-[10px] leading-tight text-zinc-950">
              {formatDosage(medication)}
            </p>
          </div>
          <div className={cellClass(2)}>
            <p className="text-[10px] leading-tight text-zinc-950">
              {medication.doseRoute?.trim() || DASH}
            </p>
          </div>
          <div className={cellClass(3)}>
            <p className="text-[10px] leading-tight text-zinc-950">
              {formatIsoDate(medication.startDatetime, DASH)}
            </p>
          </div>
          <div className={cellClass(4)}>
            <p className="text-[10px] leading-tight text-zinc-950">
              {formatIsoDate(medication.endDatetime, DASH)}
            </p>
          </div>
          <div className={cellClass(5)}>
            <a
              href={`#${anchorId}`}
              className="text-[10px] leading-tight text-blue-600 hover:underline"
              aria-label={`Jump to medication details for ${getMedicationDisplayName(medication, idx)}`}
              onClick={(event) => handleTemplateAnchorClick(event, anchorId)}
            >
              {pageNumber}
            </a>
          </div>
        </div>
      );
    })}
  </div>
);

const PanelField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <p className="text-[10px] leading-tight font-bold text-zinc-950">{label}</p>
    <p className="text-[10px] leading-tight whitespace-pre-line text-zinc-950">
      {value}
    </p>
  </div>
);

const PractitionerColumn = ({
  practitioner,
}: {
  practitioner?: TemplatePractitioner;
}) => (
  <div className="flex min-w-0 flex-1 flex-col gap-2 text-[10px] leading-tight text-zinc-950">
    <p>
      <span className="font-bold">Practitioner:</span>{' '}
      {formatPractitionerNameWithRole(practitioner, DASH)}
    </p>
    <p>
      <span className="font-bold">Phone:</span>{' '}
      {formatPractitionerPhone(practitioner, DASH)}
    </p>
    <p>
      <span className="font-bold">Address:</span>{' '}
      {formatPractitionerAddress(practitioner, DASH)}
    </p>
  </div>
);

const MedicationCard = ({
  medication,
  index,
  highlightMedicationId,
}: {
  medication: MedicationsTemplateRow;
  index: number;
  highlightMedicationId?: string;
}) => {
  const isHighlighted =
    !!highlightMedicationId && medication.id === highlightMedicationId;

  return (
    <article
      id={`medication-${medication.id}`}
      tabIndex={isHighlighted ? -1 : undefined}
      className={`flex flex-col overflow-hidden rounded-sm border-2 bg-white shadow-none ${
        isHighlighted ? 'border-amber-400' : 'border-gray-200'
      }`}
    >
      <header className="bg-slate-700 px-5 py-4">
        <p className="text-[14px] leading-tight font-semibold text-white">
          {getMedicationDisplayName(medication, index)}
        </p>
      </header>

      <div className="px-5 py-4">
        <div className="overflow-hidden rounded-sm border border-zinc-200 bg-zinc-50">
          <div className="grid grid-cols-4 gap-4 px-4 py-3">
            <PanelField
              label="Start"
              value={formatIsoDate(medication.startDatetime)}
            />
            <PanelField
              label="End"
              value={formatIsoDate(medication.endDatetime)}
            />
            <PanelField label="Dose" value={formatDosage(medication)} />
            <PanelField
              label="Route"
              value={medication.doseRoute?.trim() || DASH}
            />
          </div>

          <div className="flex flex-col gap-2 border-t border-zinc-200 px-4 py-3">
            <p className="text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              <span className="font-bold">Notes:</span>{' '}
              {getMedicationNotes(medication)}
            </p>
            <p className="text-[10px] leading-tight whitespace-pre-line text-zinc-950">
              <span className="font-bold">Instructions:</span>{' '}
              {getMedicationInstructions(medication)}
            </p>
          </div>

          {medication.practitioner ? (
            <div className="border-t border-zinc-200 px-4 py-3">
              <PractitionerColumn practitioner={medication.practitioner} />
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export const MedicationsTemplate = ({
  medications,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightMedicationId,
  reportTitle = 'Medications Report',
}: MedicationsTemplateProps) => {
  const items = sortMedicationsByRecency(medications ?? []);
  const hasItems = items.length > 0;

  return (
    <DocumentTemplateShell
      title={reportTitle}
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
    >
      <TemplateAppendixLayout
        hasItems={hasItems}
        emptyMessage="No medications available"
        summarySectionClassName="flex flex-col gap-5 px-8 py-5"
        appendixSectionClassName="flex flex-1 flex-col gap-5 px-8 py-5"
        summaryHeader={
          <TemplateSummaryHeader
            title="All Reported Medications"
            subtitle="Sorted by recency"
            note="*Refer to appendix below for more details"
          />
        }
        summaryTable={
          <SummaryTable
            medications={items}
            highlightMedicationId={highlightMedicationId}
          />
        }
        appendix={items.map((medication, index) => (
          <MedicationCard
            key={medication.id ?? `med-${index}`}
            medication={medication}
            index={index}
            highlightMedicationId={highlightMedicationId}
          />
        ))}
      />
    </DocumentTemplateShell>
  );
};
